import { useState } from "react";
import { useTranslation } from "react-i18next";

import { HAN_SEARCH_TAB_BY_KIND, type HaNSearchKind, type HaNSearchResult, useHaNSearch } from "@lib/logic";
import { useRequiredContext } from "@lib/utils";
import { Loader } from "@atoms";
import { cn } from "@utils";

import { HaNResultList } from "../shared/HaNResultList/HaNResultList";
import { HaNContext } from "../shell/HaNContext";
import { AreaSearchResult } from "./components/AreaSearchResult";
import { FacilitySearchResult } from "./components/FacilitySearchResult";
import { HaNSearchResultGroup } from "./components/HaNSearchResultGroup";
import { HaNSearchResultItem } from "./components/HaNSearchResultItem";
import { PersonSearchResult } from "./components/PersonSearchResult";
import { RoomSearchResult } from "./components/RoomSearchResult";
import { HaNSearchContext } from "./HaNSearchContext";
import { useOpenSearchResult } from "./useOpenSearchResult";

const GROUP_TITLE_KEY_BY_KIND = {
  room: "hereAndNow:Rooms",
  area: "hereAndNow:Desks",
  person: "hereAndNow:People",
  facility: "hereAndNow:Facilities",
} as const satisfies Record<HaNSearchKind, string>;

const NO_KINDS: ReadonlySet<HaNSearchKind> = new Set();

const renderResult = ({ entry, ranges }: HaNSearchResult) => {
  switch (entry.kind) {
    case "room":
      return (
        <RoomSearchResult
          room={entry.resource}
          ranges={ranges}
        />
      );
    case "area":
      return (
        <AreaSearchResult
          area={entry.resource}
          ranges={ranges}
        />
      );
    case "person":
      return (
        <PersonSearchResult
          person={entry.person}
          reservation={entry.reservation}
          ranges={ranges}
        />
      );
    case "facility":
      return (
        <FacilitySearchResult
          facility={entry.resource}
          ranges={ranges}
        />
      );
  }
};

const FADE_IN_CLASS_NAME =
  "flex flex-auto flex-col motion-safe:duration-200 motion-safe:animate-in motion-safe:fade-in";

export const HaNSearchResults = () => {
  const { t } = useTranslation();
  const { settledQuery, kinds } = useRequiredContext(HaNSearchContext);
  const { tab, building, floor, people, isResourcesLoading } = useRequiredContext(HaNContext);

  const openResult = useOpenSearchResult();
  const [expanded, setExpanded] = useState({ query: settledQuery, kinds: NO_KINDS });

  const { status, groups, hasPeopleSearchFailed } = useHaNSearch({
    query: settledQuery,
    buildingId: building.buildingId,
    tab,
    floorId: floor.floorId,
    kinds,
    reservations: people.reservations,
    isDataLoading:
      !building.isSettled || isResourcesLoading || (kinds.includes("person") && people.status === "loading"),
  });

  // A new query shows each group's top results again
  const expandedKinds = expanded.query === settledQuery ? expanded.kinds : NO_KINDS;

  const toggleExpanded = (kind: HaNSearchKind) => {
    const nextKinds = new Set(expandedKinds);

    if (nextKinds.has(kind)) {
      nextKinds.delete(kind);
    } else {
      nextKinds.add(kind);
    }

    setExpanded({ query: settledQuery, kinds: nextKinds });
  };

  if (status !== "done") {
    return (
      <div
        key="searching"
        role="status"
        className={cn(FADE_IN_CLASS_NAME, "items-center justify-center p-8")}>
        <Loader size="32px" />
        <span className="sr-only">{t("hereAndNow:Searching")}</span>
      </div>
    );
  }

  const peopleSearchFailedNote = hasPeopleSearchFailed && (
    <p className="px-5 py-4 text-sm text-mapiq-black-500">{t("hereAndNow:PeopleSearchFailed")}</p>
  );

  if (groups.length === 0) {
    return (
      <div
        key="empty"
        className={FADE_IN_CLASS_NAME}>
        <HaNResultList.Empty text={t("hereAndNow:NoSearchResults")} />
        {peopleSearchFailedNote}
      </div>
    );
  }

  const shownGroups = groups.map(group => {
    const isExpanded = expandedKinds.has(group.kind);

    return { ...group, isExpanded, shown: isExpanded ? group.allResults : group.results };
  });
  const shownResults = shownGroups.flatMap(group => group.shown);

  return (
    <div
      key="results"
      className={FADE_IN_CLASS_NAME}>
      {shownGroups.map(group => {
        return (
          <HaNSearchResultGroup
            key={group.kind}
            title={t(GROUP_TITLE_KEY_BY_KIND[group.kind])}
            allCount={group.allResults.length}
            canExpand={group.allResults.length > group.results.length}
            isExpanded={group.isExpanded}
            onToggleExpanded={() => toggleExpanded(group.kind)}>
            {group.shown.map(result => {
              return (
                <HaNSearchResultItem
                  key={result.entry.id}
                  tab={HAN_SEARCH_TAB_BY_KIND[group.kind]}
                  selectionId={result.entry.id}
                  onSelect={() => {
                    openResult(result, {
                      position: shownResults.indexOf(result) + 1,
                      resultCount: shownResults.length,
                      isExpanded: group.isExpanded,
                    });
                  }}>
                  {renderResult(result)}
                </HaNSearchResultItem>
              );
            })}
          </HaNSearchResultGroup>
        );
      })}
      {peopleSearchFailedNote}
    </div>
  );
};
